import { createId } from '@/core/id';
import { chatError } from '@/core/types';
import { findAnswer, MOCK_FALLBACK, MOCK_FALLBACK_ACTIONS, MOCK_GREETING, MOCK_GREETING_SUGGESTIONS, } from './knowledge';
const AI_AUTHOR = { id: 'caira', name: 'CAIRA', kind: 'ai' };
/**
 * In-memory transport. Mirrors the timing and event shape of a real streaming
 * backend so the UI built against it needs no changes when the server lands.
 */
export class MockTransport {
    name = 'mock';
    capabilities;
    opts;
    history = new Map();
    constructor(options = {}) {
        this.opts = {
            thinkingMs: options.thinkingMs ?? 650,
            chunkMs: options.chunkMs ?? 18,
            streaming: options.streaming ?? true,
            errorRate: options.errorRate ?? 0,
        };
        this.capabilities = {
            streaming: this.opts.streaming,
            push: false,
            history: true,
            feedback: true,
            handoff: true,
        };
    }
    async createConversation(_input) {
        await wait(120);
        const conversation = {
            id: createId('conv'),
            createdAt: Date.now(),
            status: 'open',
        };
        const greeting = {
            id: createId('msg'),
            conversationId: conversation.id,
            role: 'assistant',
            content: MOCK_GREETING,
            status: 'complete',
            createdAt: Date.now(),
            updatedAt: Date.now(),
            author: AI_AUTHOR,
            suggestions: MOCK_GREETING_SUGGESTIONS,
        };
        this.history.set(conversation.id, [greeting]);
        return conversation;
    }
    async loadHistory(conversationId) {
        await wait(80);
        return this.history.get(conversationId) ?? [];
    }
    async sendMessage({ conversationId, message, replyId }, emit, signal) {
        this.record(conversationId, message);
        emit({ type: 'typing', isTyping: true });
        await wait(this.opts.thinkingMs, signal);
        if (Math.random() < this.opts.errorRate) {
            emit({ type: 'typing', isTyping: false });
            emit({
                type: 'message.error',
                messageId: replyId,
                error: chatError('server', 'The assistant could not be reached.'),
            });
            return;
        }
        const answer = findAnswer(message.content);
        const text = answer?.reply ?? MOCK_FALLBACK;
        const now = Date.now();
        const reply = {
            id: replyId,
            conversationId,
            role: 'assistant',
            content: '',
            status: this.opts.streaming ? 'streaming' : 'pending',
            createdAt: now,
            updatedAt: now,
            author: AI_AUTHOR,
        };
        emit({ type: 'typing', isTyping: false });
        emit({ type: 'message.start', message: reply });
        if (this.opts.streaming) {
            for (const chunk of chunkify(text)) {
                if (signal.aborted) {
                    emit({ type: 'message.complete', messageId: replyId });
                    return;
                }
                emit({ type: 'message.delta', messageId: replyId, delta: chunk });
                await wait(this.opts.chunkMs, signal).catch(() => undefined);
            }
        }
        else {
            emit({ type: 'message.delta', messageId: replyId, delta: text });
        }
        if (answer?.sources?.length) {
            emit({ type: 'message.sources', messageId: replyId, sources: answer.sources });
        }
        const actions = answer?.actions ?? (answer ? undefined : MOCK_FALLBACK_ACTIONS);
        if (actions?.length) {
            emit({ type: 'message.actions', messageId: replyId, actions });
        }
        if (answer?.suggestions?.length) {
            emit({ type: 'message.suggestions', messageId: replyId, suggestions: answer.suggestions });
        }
        emit({ type: 'message.complete', messageId: replyId });
        this.record(conversationId, { ...reply, content: text, status: 'complete' });
    }
    async sendFeedback(_input) {
        await wait(150);
    }
    async requestHandoff(conversationId) {
        await wait(400);
        void conversationId;
    }
    record(conversationId, message) {
        const list = this.history.get(conversationId) ?? [];
        list.push(message);
        this.history.set(conversationId, list);
    }
}
/** Split on word boundaries so the typewriter reads naturally, not char-by-char. */
function chunkify(text) {
    return text.match(/\S+\s*/g) ?? [text];
}
function wait(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted)
            return reject(new DOMException('Aborted', 'AbortError'));
        const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        };
        const timer = setTimeout(() => {
            // Detach eagerly: a streamed reply calls wait() once per chunk, and
            // leaked listeners would pile up on the single per-message signal.
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
